/**
 * "Scheduled run" (TRA-1475): staff set when a team's GRPO run starts on
 * its own, pause it, and start one now.
 *
 * The api keeps one schedule per team. A save of an enabled schedule is
 * accepted only when a run of it would start now, so what the form shows
 * after a save is a schedule the timer can run. "Run now" starts the run
 * the timer would start, from what is saved, after a confirmation: it
 * rents a GPU node.
 *
 * Mounted by the Training page's data menu only while it is open. Both
 * mutations live here, so each answer lands while the dialog is mounted.
 */

import { useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useRunTrainingScheduleNow,
  useSaveTrainingSchedule,
  useTrainingGrpoOptions,
  useTrainingSchedule,
} from '@/hooks/useTrainingRuns'

import { ScheduledRunForm } from './ScheduledRunForm'

export function ScheduledRunDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const schedule = useTrainingSchedule()
  const grpo = useTrainingGrpoOptions(true)
  const save = useSaveTrainingSchedule()
  const start = useRunTrainingScheduleNow()
  const [confirming, setConfirming] = useState(false)
  const busy = save.isPending || start.isPending
  const failed = schedule.isError ? schedule.error : grpo.isError ? grpo.error : null

  return (
    <>
      <Dialog
        open
        onOpenChange={(next) => {
          // Escape, the overlay and the corner X all land here; none of them
          // may dismiss a save or a start that is still running.
          if (!next && !busy) onClose()
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Scheduled run</DialogTitle>
            <DialogDescription>
              Start a GRPO run for this team on the days and time you pick.
            </DialogDescription>
          </DialogHeader>
          {failed !== null ? (
            <p className="text-sm text-destructive">
              {getApiErrorDetail(failed) ?? 'The scheduled run did not load.'}
            </p>
          ) : schedule.data === undefined || grpo.data === undefined ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <ScheduledRunForm
              key={schedule.data.schedule?.updated_at ?? 'none'}
              data={schedule.data}
              grpo={grpo.data}
              isSaving={save.isPending}
              isStarting={start.isPending}
              onCancel={onClose}
              onSave={(update) => {
                save.mutate(
                  { data: update },
                  {
                    onSuccess: () => {
                      toast.success('Scheduled run saved')
                    },
                  },
                )
              }}
              onRunNow={() => {
                setConfirming(true)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Start a run now?"
        description="This starts one 8xH200 or 8xH100 node now and trains what the schedule would train. The schedule keeps its next run."
        confirmLabel="Start run"
        pendingLabel="Starting..."
        isPending={start.isPending}
        onConfirm={() => {
          start.mutate(undefined, {
            onSuccess: () => {
              toast.success('The scheduled run started.')
              setConfirming(false)
              onClose()
            },
            onError: () => {
              setConfirming(false)
            },
          })
        }}
      />
    </>
  )
}
